import { Component } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import simplify from '@turf/simplify';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { DataLayerGeojsonPointComponent } from './data-layer-geojson-point.component';
import { NWSAlertOptionsComponent} from './nwsalert-options-component/nwsalert-options-component.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';

declare var L;

/*
 * data-layer-geojson-point-weather defines a class to display weather watches and warnings.  The data format is geojson.
 *	The "Description" is displayed upon clicking the station marker.
 *
 * Example of the data file:
 * --------------------------------
 * {"type": "FeatureCollection",
 *	"crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
 *	"created_from":"https://forecast.weather.gov/wwamap/wwatxtget.php",
 *	"features": [
 *	{"type": "Feature", "properties": { "Class":"FLS", "Name":"Flood Statement", "Description":"Flood Statement\nNational ..."
 *	}, "geometry": { "type": "Point", "coordinates": [-118.85,49.00]}},
 *	  ...
 *	]
 * }
 * --------------------------------
 *
 * Within the configuration file, weather warnings can be included:
 * "overlayLayers": [
		{	"id": "weatherLayer",
			"label": "Weather Watch/Alert",
			"class": "data-layer-geojson-point-weather",
			"eventTypes": ["High Wind Warning,Gale Watch"],
			"refresh": false
		}],
 * ...
 * ]
 * 
 * Previously, a python script was run to collect National Weather Service data by scraping their website.
 * Now, we are using the api, such as:
 * https://api.weather.gov/alerts/active?status=actual&message_type=alert,update&event=Flood Warning,Flash Flood Warning,Flood Advisory,Severe Thunderstorm Warning,Special Marine Warning,Tornado Warning
 * 
 * General information found at https://www.weather.gov/documentation/services-web-api
 * Types available from https://api.weather.gov/alerts/types
 */

@Component({
	selector: 'data-layer-geojson-point-weather',
	template: `
		<div class="checkboxDiv">
			<label class="overlay-layer">
			<input type="checkbox" (click)="menuControlSelected($event)" [disabled]="isCheckboxDisabled">{{htmlElementLabel}}
			<input type="button" (click)="showAlertOptions()" value="?" class="infoButton">
			</label>
			<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPointWeatherComponent extends DataLayerGeojsonPointComponent {

	http;
	filterBy = "eventType";
	stateSelected = "CO";
	// selected event types
	selectedEventTypes: any[] = []; // ["Flood Advisory"];
	// default types - doesn't change after initialization
	// passed to popup to enable users to reset
	defaultEventTypes: any[] = [];

	// just add &event=string of events separated by commas
	baseURLAlertType = 'https://api.weather.gov/alerts/active?status=actual&message_type=alert,update&event=';

	// just add 2 character state string
	baseURLState = 'https://api.weather.gov/alerts/active/area/';

	// sometimes the response from NWS is a little slow.  If users continue to click
	// the checkbox, multiple layers can get created, leaving them permanently displayed
	isCheckboxDisabled = false;

	constructor(
		http: HttpClient, 
		mapService: MapService, 
		dataLayerService: DataLayerService, 
		configService: ConfigService, 
		private dialogService: DialogService,
		userMessageService: UserMessageService ) {

		super(mapService, dataLayerService, configService, userMessageService);
		// set url default
		// this.url = 'https://api.weather.gov/alerts/active?status=actual&message_type=alert,update&event=Flood Warning,Flash Flood Warning,Flood Advisory,Severe Thunderstorm Warning,Special Marine Warning,Tornado Warning,Blizzard Warning,Snow Squall Warning,Fire Warning';
		this.setSelectedEventTypes();
		this.http = http;
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}
	}

	showAlertOptions(): void {
		// Open the state selector
		const ref = this.dialogService.open ( NWSAlertOptionsComponent, {
			header: 'National Weather Service Warnings and Alerts',
			width: '50%',
			closable: true,
			data: { 
				filterBy: this.filterBy,
				selectedState: this.stateSelected,
				selectedEventTypes: this.selectedEventTypes,
				defaultEventTypes: this.defaultEventTypes
				}
			// style: 'overflow: hidden'
		});

		ref.onClose.subscribe((data) => {
			if ( data != null ) {
				console.log(data);
				const filterBy = data?.filterBy;
				const selectedState = data?.selectedState;
				const eventTypes = data?.selectedEventTypes;

				// apply changes
				if ( filterBy != undefined ) {
					// console.log(eventTypes);
					this.filterBy = filterBy;
					if ( filterBy == 'state') {
						this.stateSelected = selectedState;
						this.closeAlertOptions();
					} else {
						// event type
						this.selectedEventTypes = eventTypes;
						this.closeAlertOptions();
					}
				}
			}
		 })
	}

	// Close the state selector
	// Dialog closes itself.  If any final cleanup were needed, create a subscription above
	closeAlertOptions() {
		this.refreshLayer();
	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}
	setData ( someData: any ) {
		super.setData(someData);

		if ( someData.hasOwnProperty('eventTypes') && someData.eventTypes.length > 0 ) {
			console.log(someData.eventTypes);
			const addnlEvents = someData.eventTypes.toString();
			const addnlEventsArray = addnlEvents.split(",");
			this.selectedEventTypes = [...this.selectedEventTypes, ...addnlEventsArray];
			this.defaultEventTypes = JSON.parse(JSON.stringify(this.selectedEventTypes));
		}

		if ( someData.hasOwnProperty('state')) {
			this.stateSelected = someData.state;
		}
	}

	// first check whether the information has been read.
	// display the markers on the map and set the layer to active
	activateLayer () {
		this.isCheckboxDisabled = true;
		console.log('Activating ' + this.id + ' layer.');
		if ( this.mapLayer != null ) {
				this.mapService.map.removeLayer(this.mapLayer);
		}
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established

			this.parseDataFile();
		} else {
			this.drawMarkers();
		}

		this.active = true;
	}

	drawMarkers () {
		console.log('Drawing ' + this.value.features.length + ' features (half are polygons and half are points).');
		this.mapLayer = L.geoJson(this.value, {
			// placeMarkers using configuration which specifies which property should be used as the title, popup, etc.
			pointToLayer: this.placeMarkers,
			onEachFeature: this.onEachPointFeature
		}).addTo(this.mapService.map);
		this.isCheckboxDisabled = false;
	}

	// extended classes should implement this function
	onEachPointFeature(feature, layer) {
		let popupContent = '<pre>' + feature.properties.description + '</pre>';
		const options = { maxHeight: 500, minWidth: 500 };
		const tooltipContent = 'Click for detailed information';

		if (feature.properties && feature.properties.popupContent) {
			// This will append content from a "popupContent" property if it is in the GeoJSON file
			popupContent += feature.properties.popupContent;
		}

		layer.bindPopup(popupContent, options);
		layer.bindTooltip(tooltipContent);
	}

	// extended classes should implement this function
	placeMarkers(feature, latlng) {
		const marker = L.ExtraMarkers.icon({
				icon: 'fa-number',
				markerColor: 'yellow',			// marker background color
				shape: 'square',
				prefix: 'fa',
				number: 'NWS'
			});
		marker.options.shadowSize = [50, 75];
		// marker.options.title = "hello";
		return L.marker(latlng, {icon: marker});
	}

	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			this.mapService.map.removeLayer(this.mapLayer);
			this.active = false;
		}

	}

	// from data-layer-service.parseCameraStations
	parseDataFile () {
		console.log('In data-layer-geojson-point-weather.parseDataFile.');
		const self = this;

		this.getNWSData().subscribe ( weatherData => {
			// console.log( weatherData );

			// Process each feature.  There are 2 categories:
			//  * features that are multiPolygons and require additional data retrieval to get those polygons (CASE 1)
			//  * features that are a single polygon, which is already included in data retrieved (CASE 2)
			const pointFeaturesArray = [];
			const numFeatures = weatherData.features.length;  // Total number of features retrieved
			let numProcessed = 0;	// Tracks the number of features that have been processed
			console.log(numFeatures + ' NWS features to process');
			for ( let i = 0; i < numFeatures; i++ ) {
				// Now, the problem with the data is only the polygon information was returned.
				// We also want a point so a marker can be placed.
				// The solution is to copy each feature and duplicate what is needed for points as well.
				const point = JSON.parse(JSON.stringify(weatherData.features[i]));

				// Some data types return features with "geometry": null but properties.affectedZones set to a url which returns a multipolygon
				// Let's collect those and set geometry to that.
				if ( point.geometry == null && point.properties?.affectedZones != null ) {
					// CASE 1
					// loop through affectedZones and put together 
					const affectedZones = point.properties.affectedZones;
					const affectedZonesObservableArray: any[] = [];

					// Create an array of http requests and the subsequent observables
					for ( let j = 0; j < affectedZones.length; j++ ) {
						affectedZonesObservableArray.push ( this.http.get(affectedZones[j]).pipe (
							catchError( error => of(null))));
					}
					const affectedZonesObservables = forkJoin(affectedZonesObservableArray);

					// Process all the responses.
					let combinedCoordinates = [];
					affectedZonesObservables.subscribe ({
						next: value => {
							// console.log ( affectedZones );
							// console.log ( 'here', value );
							for ( let j = 0; j < value.length; j++ ) {
								if ( value[j] != null && value[j]?.['geometry'] != undefined && value[j]['geometry']?.['coordinates'] != undefined) {
									if ( value[j]['geometry']?.['type'] === 'Polygon' )
										combinedCoordinates = combinedCoordinates.concat(value[j]['geometry']['coordinates']);
									else {  // MultiPolygon
										for ( let k = 0; k < value[j]['geometry']['coordinates'].length; k++) {
											combinedCoordinates = combinedCoordinates.concat(value[j]['geometry']['coordinates'][k]);
										}
									}
								}
							}
							// console.log ( combinedCoordinates );
							// both the polygon and point need to get the collected geometry
							weatherData.features[i].geometry = {
								'type': "MultiPolygon",
								'coordinates': [ combinedCoordinates ]
							};
							point.geometry = {
								'type': "MultiPolygon",
								'coordinates': [ combinedCoordinates ]
							};
							// console.log('New point', point);
						},
						complete: () => {
							// Change the point from polygon to point
							self.findPointFromPolygon ( pointFeaturesArray, point );

							numProcessed++;
							self.checkParseDataFileDone ( weatherData, pointFeaturesArray, numProcessed, numFeatures );
						}
					})
				} else if ( point.geometry == null ) { // affectedZones is null
					console.log('Both geometry AND affected zones are null - this should not happen');
				} else {
					// CASE 2
					// Change the point from polygon to point
					self.findPointFromPolygon ( pointFeaturesArray, point );

					numProcessed++;
					self.checkParseDataFileDone ( weatherData, pointFeaturesArray, numProcessed, numFeatures );
				}

			}
			// console.log ( dataLayer );

		}, error => {
			console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + self.url + ' for all ' + self.id + ' weather watches and warnings was UNSUCCESSFUL.');

		});
	}

	// Check if all features have been processed.  If so, set class value and valueObservable to the weather data.
	checkParseDataFileDone ( weatherData, pointFeaturesArray, count, total ) {
		// console.log(count + ' out of ' + total );

		// All data has been processed if the current count is equal to the total that needed processing
		if ( count == total ) {
			console.log('National Weather Service results: ', weatherData.features.length + ' Watches/Warnings');

			// Combine the polygon data with the point data so both can be displayed together.
			weatherData.features = weatherData.features.concat(pointFeaturesArray);

			// Set value and valuObservable so layer knows to draw.
			// console.log(weatherData);
			this.value = weatherData;
			this.valueObservable.next(weatherData);
			console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
			this.url + ' for all ' + this.id + ' weather watches and warnings was successfully completed.');
		}

	}

	getNWSData(): Observable<any> {
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'accept', 'application/geo+json' );
		// httpHeaders = httpHeaders.append ( 'User-Agent', '(NovaStar Operator, NovaStar-support@trilynx.systems)' );
		const httpOptions = { headers: httpHeaders };
		this.url = this.createURL();
		return this.http.get( this.url, httpOptions );
	}

	// Given a point with geometry of polygon or multipolygon, changes the geometry to a single point whose value is the first vertex from the polygon(s).
	// Note, everything else remains the same including the description of the warning.
	// Adds the point to the pointFeaturesArray.
	findPointFromPolygon ( pointFeaturesArray, point ) {
		// console.log(point);
		if ( point.geometry != null && point.geometry.coordinates != null && point.geometry.coordinates.length > 0 ) {
			let coords = point.geometry.type === "Polygon" ? point.geometry.coordinates[0] : point.geometry.type === "MultiPolygon" ? point.geometry.coordinates[0][0] : null;
			if ( coords != null ) {
				// console.log(coords);
				const deleteCount = coords.length - 1;

				// console.log(coords);
				// console.log(deleteCount);
				// console.log(point.geometry.type);
				point.geometry.type = "Point";

				// delete all but the first coordinate
				coords.splice(1, deleteCount);
				point.geometry.coordinates = coords[0];
				pointFeaturesArray.push ( point );
			}
		}
	}

	createURL () {
		let url = "";
		if ( this.filterBy == 'state') {
			url = this.baseURLState + this.stateSelected;
		} else {
			// event type
			// console.log(this.selectedEventTypes);
			url = this.baseURLAlertType + this.selectedEventTypes.toString();
		}

		return url;
	}

	setSelectedEventTypes() {
		console.log("setting event types");
		this.selectedEventTypes = [
			"911 Telephone Outage Emergency",
			"Arroyo And Small Stream Flood Advisory",
			"Blizzard Warning",
			"Blizzard Watch",
			"Blowing Dust Advisory",
			"Blowing Dust Warning",
			"Brisk Wind Advisory",
			"Coastal Flood Advisory",
			"Coastal Flood Statement",
			"Coastal Flood Warning",
			"Coastal Flood Watch",
			"Evacuation Immediate",
			"Extreme Fire Danger",
			"Extreme Wind Warning",
			"Fire Warning",
			"Fire Weather Watch",
			"Flash Flood Statement",
			"Flash Flood Warning",
			"Flash Flood Watch",
			"Flood Advisory",
			"Flood Statement",
			"Flood Warning",
			"Flood Watch",
			"High Wind Warning",
			"High Wind Watch",
			"Hurricane Force Wind Warning",
			"Hurricane Force Wind Watch",
			"Hurricane Local Statement",
			"Hurricane Warning",
			"Hurricane Watch",
			"Lakeshore Flood Advisory",
			"Lakeshore Flood Statement",
			"Lakeshore Flood Warning",
			"Lakeshore Flood Watch",
			"Severe Thunderstorm Warning",
			"Severe Thunderstorm Watch",
			"Severe Weather Statement",
			"Small Stream Flood Advisory",
			"Snow Squall Warning",
			"Special Weather Statement",
			"Storm Surge Warning",
			"Storm Surge Watch",
			"Storm Warning",
			"Storm Watch",
			"Tornado Warning",
			"Tornado Watch",
			"Tropical Depression Local Statement",
			"Tropical Storm Local Statement",
			"Tropical Storm Warning",
			"Tropical Storm Watch",
			"Tsunami Advisory",
			"Tsunami Warning",
			"Tsunami Watch",
			"Typhoon Local Statement",
			"Typhoon Warning",
			"Typhoon Watch",
			"Urban And Small Stream Flood Advisory",
			"Wind Advisory"
		];

		this.defaultEventTypes = JSON.parse(JSON.stringify(this.selectedEventTypes));
	}

}
